import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { SessionAvatar } from '@/components/common/SessionAvatar';
import { ROUTES } from '@/constants/routes';
import type { CurrentUser } from '@/types/auth.types';
import { formatRelativeTime, formatShortDate } from '@/utils/dateUtils';

export interface ProfileCardProps {
  user: CurrentUser;
}

/** The populated state of `ProfilePage` — read-only summary plus links into Change Password / MFA setup. */
export function ProfileCard({ user }: ProfileCardProps) {
  return (
    <Card>
      <CardContent>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <SessionAvatar size={64} />
            <Stack spacing={0.5}>
              <Typography variant="h6">
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {user.roles.map((role) => (
              <Chip key={role} label={role} size="small" />
            ))}
          </Stack>

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center">
            <VerifiedUserOutlinedIcon
              fontSize="small"
              color={user.status === 'Active' ? 'success' : 'disabled'}
            />
            <Typography variant="body2">Account status: {user.status}</Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <ShieldOutlinedIcon fontSize="small" color={user.mfaEnabled ? 'success' : 'disabled'} />
            <Typography variant="body2">
              Multi-factor authentication: {user.mfaEnabled ? 'Enabled' : 'Not enabled'}
            </Typography>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Member since {formatShortDate(user.createdAt)}
            {user.lastLoginAt ? ` · Last sign-in ${formatRelativeTime(user.lastLoginAt)}` : ''}
          </Typography>

          <Stack direction="row" spacing={1.5}>
            <Button
              component={RouterLink}
              to={ROUTES.changePassword}
              variant="outlined"
              size="small"
            >
              {user.hasPassword ? 'Change password' : 'Set WillnQ password'}
            </Button>
            <Button component={RouterLink} to={ROUTES.mfaSetup} variant="outlined" size="small">
              Manage MFA
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
